/**
 * The needs-repair status on item lists and headers (spec UI 10). `ok` renders nothing, and so does
 * every row with the repair flag off, because the server then sends `ok` for each.
 *
 * The chip reads "Needs repair"; the rest of the label ("…: warehouse account removed") is spoken to
 * screen readers and, with `withTooltip`, shown on hover. Lists pass no tooltip: their cells sit inside
 * the row's own button.
 */
import type { AccountRepairView } from '@server/warehouse-repair-state';

import { Badge } from '@/_shared/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/_shared/components/ui/tooltip';

import { NEEDS_REPAIR_LABEL, NEEDS_REPAIR_LABEL_FULL, UNASSIGNED_LABEL_FULL, UNVERIFIED_LABEL } from './copy';

const LABELS: Record<Exclude<AccountRepairView['state'], 'ok'>, { short: string; full: string }> = {
  needs_repair: { short: NEEDS_REPAIR_LABEL, full: NEEDS_REPAIR_LABEL_FULL },
  unassigned: { short: NEEDS_REPAIR_LABEL, full: UNASSIGNED_LABEL_FULL },
  unverified: { short: UNVERIFIED_LABEL, full: `${UNVERIFIED_LABEL}: its account could not run all its queries` },
};

export function NeedsRepairBadge({
  state,
  withTooltip = false,
  listRow = false,
  className,
}: {
  state: AccountRepairView['state'];
  withTooltip?: boolean;
  /** A list row labels only needs repair (spec UI 10); an unverified item is labelled on its page. */
  listRow?: boolean;
  className?: string;
}): React.ReactElement | null {
  if (state === 'ok' || (listRow && state === 'unverified')) return null;
  const { short, full } = LABELS[state];
  const badge = (
    <Badge variant={state === 'unverified' ? 'info' : 'warning'} className={className}>
      {short}
      <span className="sr-only">{full.slice(short.length)}</span>
    </Badge>
  );
  if (!withTooltip) return badge;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent>{full}</TooltipContent>
    </Tooltip>
  );
}
